import React, { useState } from 'react';
import { X } from 'lucide-react';

export default function ThemeSettingsModal({ theme, onClose, onSave }) {
  const [formData, setFormData] = useState({
    salon_name: theme?.salon_name || 'Luxe Salon',
    tagline: theme?.tagline || 'Style & Beauty Redefined',
    primary_color: theme?.primary_color || '#8B7355',
    accent_color: theme?.accent_color || '#5A9A5A',
    heading_font: theme?.heading_font || 'Playfair Display',
    body_font: theme?.body_font || 'Lato',
    button_style: theme?.button_style || 'rounded',
    card_radius: theme?.card_radius || '8px',
    section_spacing: theme?.section_spacing || '80px',
    header_style: theme?.header_style || 'light',
    logo_url: theme?.logo_url || '',
    favicon_url: theme?.favicon_url || ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="theme-settings-modal">
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '700px' }}>
        <div className="modal-header">
          <h2 className="modal-title">Theme Settings</h2>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="theme-settings-grid">
              {/* Brand */}
              <div className="form-group">
                <label className="form-label">Salon Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.salon_name}
                  onChange={(e) => handleChange('salon_name', e.target.value)}
                  data-testid="salon-name-input"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Tagline</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.tagline}
                  onChange={(e) => handleChange('tagline', e.target.value)}
                  data-testid="tagline-input"
                />
              </div>

              {/* Colors */}
              <div className="form-group">
                <label className="form-label">Primary Color</label>
                <div className="color-picker-wrapper">
                  <input
                    type="color"
                    className="color-picker"
                    value={formData.primary_color}
                    onChange={(e) => handleChange('primary_color', e.target.value)}
                    data-testid="primary-color-input"
                  />
                  <input
                    type="text"
                    className="form-input"
                    value={formData.primary_color}
                    onChange={(e) => handleChange('primary_color', e.target.value)}
                    style={{ flex: 1 }}
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label className="form-label">Accent Color</label>
                <div className="color-picker-wrapper">
                  <input
                    type="color"
                    className="color-picker"
                    value={formData.accent_color}
                    onChange={(e) => handleChange('accent_color', e.target.value)}
                    data-testid="accent-color-input"
                  />
                  <input
                    type="text"
                    className="form-input"
                    value={formData.accent_color}
                    onChange={(e) => handleChange('accent_color', e.target.value)}
                    style={{ flex: 1 }}
                  />
                </div>
              </div>

              {/* Typography */}
              <div className="form-group">
                <label className="form-label">Heading Font</label>
                <select
                  className="form-input"
                  value={formData.heading_font}
                  onChange={(e) => handleChange('heading_font', e.target.value)}
                  data-testid="heading-font-select"
                >
                  <option value="Playfair Display">Playfair Display</option>
                  <option value="Cormorant Garamond">Cormorant Garamond</option>
                  <option value="Libre Baskerville">Libre Baskerville</option>
                  <option value="Merriweather">Merriweather</option>
                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label">Body Font</label>
                <select
                  className="form-input"
                  value={formData.body_font}
                  onChange={(e) => handleChange('body_font', e.target.value)}
                  data-testid="body-font-select"
                >
                  <option value="Lato">Lato</option>
                  <option value="Open Sans">Open Sans</option>
                  <option value="Raleway">Raleway</option>
                  <option value="Source Sans Pro">Source Sans Pro</option>
                </select>
              </div>

              {/* Styles */}
              <div className="form-group">
                <label className="form-label">Button Style</label>
                <select
                  className="form-input"
                  value={formData.button_style}
                  onChange={(e) => handleChange('button_style', e.target.value)}
                  data-testid="button-style-select"
                >
                  <option value="rounded">Rounded</option>
                  <option value="pill">Pill</option>
                  <option value="sharp">Sharp</option>
                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label">Card Radius</label>
                <select
                  className="form-input"
                  value={formData.card_radius}
                  onChange={(e) => handleChange('card_radius', e.target.value)}
                  data-testid="card-radius-select"
                >
                  <option value="0px">None</option>
                  <option value="4px">Small</option>
                  <option value="8px">Medium</option>
                  <option value="16px">Large</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Section Spacing</label>
                <select
                  className="form-input"
                  value={formData.section_spacing}
                  onChange={(e) => handleChange('section_spacing', e.target.value)}
                  data-testid="section-spacing-select"
                >
                  <option value="40px">Compact</option>
                  <option value="60px">Normal</option>
                  <option value="80px">Spacious</option>
                  <option value="100px">Extra Spacious</option>
                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label">Header Style</label>
                <select
                  className="form-input"
                  value={formData.header_style}
                  onChange={(e) => handleChange('header_style', e.target.value)}
                  data-testid="header-style-select"
                >
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                  <option value="transparent">Transparent</option>
                </select>
              </div>

              {/* Logo & Favicon */}
              <div className="form-group">
                <label className="form-label">Logo URL</label>
                <input
                  type="url"
                  className="form-input"
                  value={formData.logo_url}
                  onChange={(e) => handleChange('logo_url', e.target.value)}
                  placeholder="https://..."
                  data-testid="logo-url-input"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Favicon URL</label>
                <input
                  type="url"
                  className="form-input"
                  value={formData.favicon_url}
                  onChange={(e) => handleChange('favicon_url', e.target.value)}
                  placeholder="https://..."
                  data-testid="favicon-url-input"
                />
              </div>
            </div>
          </div>
          
          <div className="modal-footer">
            <button type="button" className="toolbar-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="toolbar-btn primary" data-testid="save-theme-btn">
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
